import { ChevronRight } from '@untitledui/icons';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import { AhoyLogoMinimal } from '@/lib/foundations/logo/ahoy-logo-minimal';
import { cx } from '@/utils/cx';

import {
  ANALYZING_PLACEHOLDER,
  ANALYZING_PLACEHOLDER_DISPLAY,
} from '../../lib/proposals/analyzingPlaceholder';
import type {
  ProposalUIComponent,
  ProposalUIDefinition,
} from '../../lib/proposals/core/types';

/**
 * Import stats data for dynamic stat_row rendering.
 * Used by welcome proposal to show real-time import progress.
 */
export interface ImportStatsData {
  emailsAnalyzed: number;
  contactsFound: number;
  /** Meetings synced from the current user's own calendar integrations. */
  meetingsFound: number;
  isLoading: boolean;
}

const ImportStatsContext = createContext<ImportStatsData | null>(null);

/**
 * Save handler for editable definition_list items (welcome onboarding insights).
 * Receives the user-facing term ("Role", "Focus", ...) and the new value.
 * Resolves on successful persistence; rejects to signal the row should revert.
 */
export type InsightEditHandler = (term: string, value: string) => Promise<void>;

const InsightEditContext = createContext<InsightEditHandler | null>(null);

export const InsightEditProvider = InsightEditContext.Provider;

// Shared classes for inline-editable inputs/textareas inside the insights
// card. The visual intent is "looks like the surrounding read-only text but
// is editable on click", matching the inline <select> pattern already used
// by Timezone/Currency in WelcomeProposalContent.tsx. We deliberately avoid
// the heavier <Input>/<TextArea> wrappers from lib/base because they bake in
// ring/shadow/padding chrome that would visually break the card.
const EDITABLE_FIELD_CLASSES =
  'w-full text-sm font-medium text-fg-primary bg-transparent ' +
  'border-none rounded px-1 -mx-1 ' +
  'focus:outline-none focus:ring-1 focus:ring-brand-500 ' +
  'placeholder:text-fg-quaternary placeholder:italic ' +
  'disabled:opacity-60 disabled:cursor-progress';

interface ProposalUIRendererProps {
  uiDefinition: ProposalUIDefinition;
  className?: string;
  /** Optional import stats for dynamic stat_row rendering */
  importStats?: ImportStatsData;
  /** Optional content to render after a specific component ID */
  renderAfterComponent?: {
    afterId: string;
    content: React.ReactNode;
  };
}

/**
 * Renders a custom UI definition for proposals.
 * Supports components like sections (with collapsible variant), flex, cards, text, etc.
 */
export default function ProposalUIRenderer({
  uiDefinition,
  className = '',
  importStats,
  renderAfterComponent,
}: ProposalUIRendererProps) {
  const { layout, components } = uiDefinition;

  // Apply layout configuration — all padding managed by parent wrapper
  const layoutClasses = getLayoutClasses(layout);
  const combinedClasses =
    `proposal-ui-renderer ${layoutClasses} ${className}`.trim();

  return (
    <ImportStatsContext.Provider value={importStats ?? null}>
      <div className={combinedClasses}>
        {components.map((component, index) => (
          <React.Fragment key={component.id || `component-${index}`}>
            <ComponentRenderer component={component} />
            {renderAfterComponent &&
              component.id === renderAfterComponent.afterId &&
              renderAfterComponent.content}
          </React.Fragment>
        ))}
      </div>
    </ImportStatsContext.Provider>
  );
}

function getLayoutClasses(layout?: ProposalUIDefinition['layout']): string {
  if (!layout) {
    return '';
  }

  const { type, props = {} } = layout;

  if (type === 'container') {
    const maxWidth = (props.maxWidth as string) || 'lg';

    const maxWidthClasses: Record<string, string> = {
      sm: 'max-w-screen-sm',
      md: 'max-w-screen-md',
      lg: 'max-w-screen-lg',
      xl: 'max-w-screen-xl',
      full: 'max-w-none',
    };

    // Padding is managed by the parent wrapper — don't add it here
    return `mx-auto ${maxWidthClasses[maxWidth] || maxWidthClasses.lg}`;
  }

  return '';
}

interface ComponentRendererProps {
  component: ProposalUIComponent;
}

function ComponentRenderer({ component }: ComponentRendererProps) {
  const { type, props, children } = component;

  const renderChildren = () => {
    if (!children || children.length === 0) {
      return null;
    }
    return children.map((child, index) => (
      <ComponentRenderer key={child.id || `child-${index}`} component={child} />
    ));
  };

  switch (type) {
    case 'heading':
      return <HeadingComponent props={props} />;
    case 'text':
      return <TextComponent props={props} />;
    case 'paragraph':
      return <ParagraphComponent props={props} />;
    case 'section':
      return (
        <SectionComponent props={props}>{renderChildren()}</SectionComponent>
      );
    case 'card':
      return <CardComponent props={props}>{renderChildren()}</CardComponent>;
    case 'flex':
      return <FlexComponent props={props}>{renderChildren()}</FlexComponent>;
    case 'stat_row':
      return <StatRowComponent props={props} />;
    case 'definition_list':
      return <DefinitionListComponent props={props} />;
    case 'list':
      return <ListComponent props={props} />;
    case 'divider':
      return <DividerComponent props={props} />;
    default:
      console.warn(`Unknown component type: ${type}`);
      return null;
  }
}

// Individual component implementations

function HeadingComponent({ props }: { props: Record<string, unknown> }) {
  const { text, level = 2, align = 'left', icon } = props;
  const alignClasses: Record<string, string> = {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right',
  };

  const sizeClasses: Record<number, string> = {
    1: 'text-display-sm font-semibold',
    2: 'text-2xl font-semibold',
    3: 'text-lg font-semibold',
    4: 'text-md font-semibold',
    5: 'text-sm font-semibold',
    6: 'text-xs font-semibold',
  };

  const textContent = text as string;

  // If icon is specified, render heading with icon
  // For centered alignment with icon, we position the icon absolutely to the left of the text
  // so the text itself remains visually centered
  if (icon === 'ahoy-logo') {
    if (align === 'center') {
      return (
        <div className='flex justify-center mb-4'>
          <div className='relative inline-flex items-center'>
            <AhoyLogoMinimal className='absolute right-full mr-2 size-7 text-fg-primary -translate-y-0.5' />
            <h2 className={`${sizeClasses[2]} text-fg-primary`}>
              {textContent}
            </h2>
          </div>
        </div>
      );
    }
    const justifyClass = align === 'right' ? 'justify-end' : 'justify-start';
    return (
      <div className={`flex items-center gap-2 ${justifyClass} mb-2`}>
        <AhoyLogoMinimal className='size-7 text-fg-primary' />
        <h2 className={`${sizeClasses[2]} text-fg-primary`}>{textContent}</h2>
      </div>
    );
  }

  const className = `${sizeClasses[level as number] || sizeClasses[2]} ${alignClasses[(align as string) || 'left']} text-fg-primary mb-2`;

  switch (level) {
    case 1:
      return <h1 className={className}>{textContent}</h1>;
    case 3:
      return <h3 className={className}>{textContent}</h3>;
    case 4:
      return <h4 className={className}>{textContent}</h4>;
    case 5:
      return <h5 className={className}>{textContent}</h5>;
    case 6:
      return <h6 className={className}>{textContent}</h6>;
    default:
      return <h2 className={className}>{textContent}</h2>;
  }
}

function TextComponent({ props }: { props: Record<string, unknown> }) {
  const {
    content,
    size = 'md',
    weight = 'normal',
    color = 'primary',
    pr,
  } = props;

  const sizeClasses: Record<string, string> = {
    xs: 'text-xs',
    sm: 'text-sm',
    md: 'text-sm', // 14px for consistent body text
    lg: 'text-base',
    xl: 'text-lg',
  };

  const weightClasses: Record<string, string> = {
    normal: 'font-normal',
    medium: 'font-medium',
    semibold: 'font-semibold',
    bold: 'font-bold',
  };

  const colorClasses: Record<string, string> = {
    primary: 'text-fg-primary',
    secondary: 'text-fg-secondary',
    success: 'text-fg-success-primary',
    error: 'text-fg-error-primary',
    muted: 'text-fg-quaternary',
  };

  // Allow explicit padding-right (e.g., pr: 10 for 10px)
  const prStyle = pr ? { paddingRight: `${pr}px` } : undefined;

  const baseClasses = `${sizeClasses[(size as string) || 'md']} ${weightClasses[(weight as string) || 'normal']} ${colorClasses[(color as string) || 'primary']} pr-[10px]`;

  // Always render as div for consistent padding behavior
  return (
    <div className={baseClasses} style={prStyle}>
      {content as string}
    </div>
  );
}

function ParagraphComponent({ props }: { props: Record<string, unknown> }) {
  const { text, content, size = 'md' } = props;
  const displayText = (text || content) as string;

  const sizeClasses: Record<string, string> = {
    sm: 'text-sm',
    md: 'text-sm', // 14px for consistent body text
    lg: 'text-base',
  };

  // Default to secondary color, but can be overridden by parent (like info card's [&_p]:text-[#2a559d])
  return (
    <p
      className={`${sizeClasses[(size as string) || 'md']} text-fg-secondary mb-3 last:mb-0`}
    >
      {displayText}
    </p>
  );
}

function SectionComponent({
  props,
  children,
}: {
  props: Record<string, unknown>;
  children?: React.ReactNode;
}) {
  const {
    title: rawTitle,
    variant = 'default',
    padding = 'md',
    defaultExpanded = true,
  } = props;
  const [isExpanded, setIsExpanded] = useState(defaultExpanded as boolean);
  const title = rawTitle ? String(rawTitle) : null;

  const isCollapsible = variant === 'collapsible';

  const variantClasses: Record<string, string> = {
    default: '',
    card: 'card-refined',
    bordered: 'card-refined',
    collapsible: 'card-refined overflow-hidden',
  };

  const paddingClasses: Record<string, string> = {
    none: '',
    sm: 'p-3',
    md: 'p-4',
    lg: 'p-6',
  };

  if (isCollapsible) {
    return (
      <section className={`${variantClasses.collapsible} mb-4`}>
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className='w-full flex items-center justify-between p-4 text-left hover:bg-bg-secondary transition-colors'
        >
          {title && (
            <h3 className='text-lg font-semibold text-fg-primary'>{title}</h3>
          )}
          <ChevronRight
            className={`w-5 h-5 text-fg-quaternary transition-transform ${isExpanded ? 'rotate-90' : ''}`}
          />
        </button>
        {isExpanded && (
          <div
            className={`border-t border-primary ${paddingClasses[(padding as string) || 'md']}`}
          >
            {children}
          </div>
        )}
      </section>
    );
  }

  return (
    <section
      className={`${variantClasses[(variant as string) || 'default']} ${paddingClasses[(padding as string) || 'md']} mb-4`}
    >
      {title && (
        <h3 className='text-lg font-semibold text-fg-primary mb-3'>{title}</h3>
      )}
      {children}
    </section>
  );
}

function CardComponent({
  props,
  children,
}: {
  props: Record<string, unknown>;
  children?: React.ReactNode;
}) {
  const { variant = 'default', padding = 'md' } = props;

  const variantClasses: Record<string, string> = {
    default: 'card-refined',
    bordered: 'card-refined border border-primary',
    compact: 'card-refined bg-bg-secondary',
    // Info card uses Google-like blue from Figma: bg #eff8ff, border #4285f4
    // Use [&_p]:text-sm (14px) for consistent body text
    info: 'rounded-lg border border-[#4285f4] bg-[#eff8ff] dark:border-blue-600 dark:bg-blue-950/30 [&_p]:text-sm [&_p]:leading-5 [&_p]:text-[#2a559d] [&_h1]:text-[#2a559d] [&_h2]:text-[#2a559d] [&_h3]:text-[#2a559d] [&_h4]:text-[#2a559d] [&_div]:text-[#2a559d] dark:[&_p]:text-blue-200 dark:[&_div]:text-blue-200',
    // White card variant with subtle border for insights section
    insights:
      'rounded-lg border border-[rgba(0,0,0,0.06)] bg-white dark:bg-bg-secondary dark:border-primary',
  };

  const paddingClasses: Record<string, string> = {
    sm: 'p-2',
    md: 'p-3',
    lg: 'p-4',
  };

  return (
    <div
      className={`${variantClasses[(variant as string) || 'default']} ${paddingClasses[(padding as string) || 'md']} mb-4`}
    >
      {children}
    </div>
  );
}

function FlexComponent({
  props,
  children,
}: {
  props: Record<string, unknown>;
  children?: React.ReactNode;
}) {
  const { direction = 'row', gap = 'md', wrap = false } = props;

  const directionClasses: Record<string, string> = {
    row: 'flex-row',
    column: 'flex-col',
  };

  const gapClasses: Record<string, string> = {
    none: 'gap-0',
    sm: 'gap-2',
    md: 'gap-4',
    lg: 'gap-6',
  };

  return (
    <div
      className={`flex ${directionClasses[(direction as string) || 'row']} ${gapClasses[(gap as string) || 'md']} ${wrap ? 'flex-wrap' : ''} mb-4`}
    >
      {children}
    </div>
  );
}

interface StatItem {
  label: string;
  value: string;
  color?: 'primary' | 'success' | 'error' | 'warning' | 'muted';
}

function StatRowComponent({ props }: { props: Record<string, unknown> }) {
  const { items, variant } = props;
  const importStats = useContext(ImportStatsContext);
  let statItems = items as StatItem[];

  // For import_stats variant, use dynamic data from context when available.
  // Show "—" for zero values (data not yet available) or while loading.
  // "Contacts found" was previously shown here but was removed from the
  // welcome onboarding UI (the count is still computed server-side and could
  // be surfaced elsewhere if needed).
  if (variant === 'import_stats' && importStats && !importStats.isLoading) {
    statItems = [
      {
        label: 'Emails analyzed',
        // Show "—" if zero or not available, actual number only when > 0
        value:
          importStats.emailsAnalyzed > 0
            ? importStats.emailsAnalyzed.toLocaleString()
            : '—',
      },
      {
        label: 'Meetings found',
        // Show "—" if zero, actual number only when > 0
        value:
          importStats.meetingsFound > 0
            ? importStats.meetingsFound.toLocaleString()
            : '—',
      },
    ];
  } else if (variant === 'import_stats' && importStats?.isLoading) {
    // Show dash placeholder while loading
    statItems = [
      { label: 'Emails analyzed', value: '—' },
      { label: 'Meetings found', value: '—' },
    ];
  }

  if (!statItems || statItems.length === 0) {
    return null;
  }

  const colorClasses: Record<string, string> = {
    primary: 'text-fg-primary',
    success: 'text-fg-success-primary',
    error: 'text-fg-error-primary',
    warning: 'text-fg-warning-primary',
    muted: 'text-fg-quaternary',
  };

  return (
    <div className='flex flex-row divide-x divide-[rgba(0,0,0,0.07)] border border-[rgba(0,0,0,0.07)] rounded-lg bg-[#f9fafb] dark:bg-bg-tertiary dark:border-primary dark:divide-primary mb-4'>
      {(() => {
        const seen = new Map<string, number>();
        return statItems.map(item => {
          const count = seen.get(item.label) ?? 0;
          seen.set(item.label, count + 1);
          return (
            <div
              key={`${item.label}::${count}`}
              className='flex-1 py-4 px-5 flex items-center gap-3'
            >
              {/* Value on top, label below - matching Figma layout */}
              <div className='flex flex-col'>
                <div
                  className={`text-2xl font-medium ${colorClasses[item.color || 'primary']}`}
                >
                  {item.value}
                </div>
                <div className='text-sm text-fg-secondary'>{item.label}</div>
              </div>
            </div>
          );
        });
      })()}
    </div>
  );
}

interface DefinitionItem {
  term: string;
  definition: string;
  /**
   * When true the row renders as an inline-editable input/textarea wired
   * up to the surrounding `InsightEditContext`. Defaults to false.
   */
  editable?: boolean;
  /**
   * When `editable` is true: render a `<textarea>` (true) or single-line
   * `<input>` (false / undefined).
   */
  multiline?: boolean;
}

/**
 * One editable row inside a horizontal `definition_list`. Extracted from
 * `DefinitionListComponent` so each row owns its own draft/saving state —
 * keeps rerenders local when one row's input changes, and matches the
 * "extract components, don't inline" rule from FRONTEND_PATTERNS.md.
 *
 * Textarea rows auto-grow to fit content (no internal scrollbar, no manual
 * resize handle). While the server value is still the "Analyzing..." sentinel
 * the input is disabled so the user can't type into a field that's about to
 * be overwritten by the learning agent.
 */
function EditableDefinitionRow({ item }: { item: DefinitionItem }) {
  const saveInsight = useContext(InsightEditContext);
  const isAnalyzing = item.definition === ANALYZING_PLACEHOLDER;

  // Local draft: what the user is typing. We seed it from the server value
  // (treating the "Analyzing..." sentinel as empty so the placeholder shows).
  const initialValue = isAnalyzing ? '' : item.definition;
  const [draft, setDraft] = useState(initialValue);
  const [isSaving, setIsSaving] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Track the last value we know is in sync with the server so we can:
  //   (a) decide whether a blur should trigger a save, and
  //   (b) re-sync the local draft if the server pushes a new value
  //       (e.g. learning agent finishes) without clobbering an in-progress
  //       edit.
  const lastServerValueRef = useRef(item.definition);

  useEffect(() => {
    // Only re-sync from the server if the user hasn't drifted off it. If
    // they're typing, leave their draft alone. We treat any draft that
    // differs from the prior server value as a user edit — including an
    // intentional empty-string clear (don't special-case `draft === ''`).
    if (item.definition === lastServerValueRef.current) {
      return;
    }
    const previousServerValue =
      lastServerValueRef.current === ANALYZING_PLACEHOLDER
        ? ''
        : lastServerValueRef.current;
    const userHasDrafted = draft !== previousServerValue;
    if (!userHasDrafted) {
      setDraft(
        item.definition === ANALYZING_PLACEHOLDER ? '' : item.definition
      );
    }
    lastServerValueRef.current = item.definition;
  }, [item.definition, draft]);

  // Auto-size the textarea to fit its content. Run synchronously after every
  // render that changes the visible value so the box always matches the text
  // height — no internal scrollbar, no clipped lines. Uses useLayoutEffect to
  // avoid a one-frame flash at the wrong height. The width-controlled
  // scrollHeight measurement assumes the textarea spans the full row width
  // (see EDITABLE_FIELD_CLASSES → `w-full`).
  useLayoutEffect(() => {
    if (!item.multiline) {
      return;
    }
    const textarea = textareaRef.current;
    if (!textarea) {
      return;
    }
    textarea.style.height = 'auto';
    textarea.style.height = `${textarea.scrollHeight}px`;
  }, [draft, item.multiline]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setDraft(e.target.value);
    },
    []
  );

  const handleBlur = useCallback(async () => {
    if (!saveInsight) {
      return;
    }
    // No-op if the value hasn't actually changed from what's on the server.
    const serverValue =
      lastServerValueRef.current === ANALYZING_PLACEHOLDER
        ? ''
        : lastServerValueRef.current;
    if (draft === serverValue) {
      return;
    }
    setIsSaving(true);
    try {
      await saveInsight(item.term, draft);
      lastServerValueRef.current = draft;
    } catch {
      // Parent surfaces the toast; revert the local draft so the row reflects
      // what's actually persisted. Re-read `lastServerValueRef.current` here
      // (don't reuse the pre-request `serverValue`) so a concurrent server
      // update that landed while the save was in flight isn't overwritten by
      // a stale pre-request value.
      setDraft(
        lastServerValueRef.current === ANALYZING_PLACEHOLDER
          ? ''
          : lastServerValueRef.current
      );
    } finally {
      setIsSaving(false);
    }
  }, [draft, item.term, saveInsight]);

  // Enter commits a single-line input (matches native form UX). Ctrl/Cmd+Enter
  // commits a textarea so users can still insert newlines with a plain Enter.
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const isTextArea = e.currentTarget.tagName === 'TEXTAREA';
      const wantsCommit =
        e.key === 'Enter' && (isTextArea ? e.metaKey || e.ctrlKey : true);
      if (wantsCommit) {
        e.preventDefault();
        e.currentTarget.blur();
      }
    },
    []
  );

  const placeholder = isAnalyzing ? ANALYZING_PLACEHOLDER_DISPLAY : '';
  // Disable while the learning agent is still working (server value is the
  // "Analyzing..." sentinel) or while a save is in flight. Once the agent
  // pushes a real value the row becomes editable again.
  const isDisabled = isSaving || isAnalyzing;

  if (item.multiline) {
    return (
      <textarea
        ref={textareaRef}
        aria-label={item.term}
        value={draft}
        placeholder={placeholder}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        disabled={isDisabled}
        rows={1}
        className={cx(
          EDITABLE_FIELD_CLASSES,
          'resize-none min-h-[1.5rem] overflow-hidden'
        )}
      />
    );
  }

  return (
    <input
      type='text'
      aria-label={item.term}
      value={draft}
      placeholder={placeholder}
      onChange={handleChange}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      disabled={isDisabled}
      className={EDITABLE_FIELD_CLASSES}
    />
  );
}

function DefinitionListComponent({
  props,
}: {
  props: Record<string, unknown>;
}) {
  const { items, layout = 'default' } = props;
  const itemsList = items as DefinitionItem[];

  if (!itemsList || itemsList.length === 0) {
    return null;
  }

  // For non-editable items, keep the original behavior of suppressing
  // placeholder rows entirely until the learning agent fills them in.
  // Editable rows always render — we show "Analyzing..." as a placeholder so
  // the user can start editing immediately.
  const visibleItems = itemsList.filter(item => {
    if (item.editable) {
      return true;
    }
    return item.definition && item.definition !== ANALYZING_PLACEHOLDER;
  });

  if (visibleItems.length === 0) {
    return null;
  }

  if (layout === 'horizontal') {
    // Horizontal layout matching Figma: term on left (semibold), definition on right, with divider lines
    // Use text-sm (14px) to match Figma's 16px line-height with tighter spacing
    return (
      <dl className='rounded-lg border border-[rgba(0,0,0,0.06)] bg-white dark:bg-bg-secondary dark:border-primary overflow-hidden mb-4'>
        {(() => {
          const seen = new Map<string, number>();
          return visibleItems.map(item => {
            const count = seen.get(item.term) ?? 0;
            seen.set(item.term, count + 1);
            return (
              <div
                key={`${item.term}::${count}`}
                className='grid grid-cols-[140px_1fr] gap-4 py-2.5 px-4 border-b border-[rgba(0,0,0,0.06)] dark:border-primary last:border-b-0'
              >
                <dt className='text-sm font-semibold text-fg-primary'>
                  {item.term}
                </dt>
                <dd className='text-sm font-medium text-fg-primary'>
                  {item.editable ? (
                    <EditableDefinitionRow item={item} />
                  ) : (
                    item.definition
                  )}
                </dd>
              </div>
            );
          });
        })()}
      </dl>
    );
  }

  return (
    <dl className='space-y-3'>
      {(() => {
        const seen = new Map<string, number>();
        return visibleItems.map(item => {
          const count = seen.get(item.term) ?? 0;
          seen.set(item.term, count + 1);
          return (
            <div key={`${item.term}::${count}`}>
              <dt className='text-sm font-semibold text-fg-primary mb-1'>
                {item.term}
              </dt>
              <dd className='text-sm text-fg-secondary'>
                {item.editable ? (
                  <EditableDefinitionRow item={item} />
                ) : (
                  item.definition
                )}
              </dd>
            </div>
          );
        });
      })()}
    </dl>
  );
}

/**
 * Safely render a list item that may be a string or a {term, definition} object.
 * The backend sometimes sends definition_list-shaped data inside a list component.
 */
function renderListItemContent(item: unknown): React.ReactNode {
  if (typeof item === 'string' || typeof item === 'number') {
    return item;
  }
  if (item && typeof item === 'object') {
    const obj = item as Record<string, unknown>;
    if ('term' in obj && 'definition' in obj) {
      return (
        <>
          <span className='font-semibold'>{String(obj.term)}</span>:{' '}
          {String(obj.definition)}
        </>
      );
    }
    // Fallback for other object shapes — render as JSON to avoid crash
    return JSON.stringify(item);
  }
  return String(item);
}

function ListComponent({ props }: { props: Record<string, unknown> }) {
  const { items, variant = 'bullet' } = props;
  const itemsList = items as unknown[];

  if (!itemsList || itemsList.length === 0) {
    return null;
  }

  if (variant === 'numbered') {
    return (
      <ol className='list-decimal list-inside space-y-1.5 text-sm text-fg-secondary'>
        {(() => {
          const seen = new Map<string, number>();
          return itemsList.map(item => {
            const raw =
              typeof item === 'string' || typeof item === 'number'
                ? String(item)
                : JSON.stringify(item);
            const count = seen.get(raw) ?? 0;
            seen.set(raw, count + 1);
            return (
              <li key={count > 0 ? `${raw}::${count}` : raw}>
                {renderListItemContent(item)}
              </li>
            );
          });
        })()}
      </ol>
    );
  }

  return (
    <ul className='list-disc list-inside space-y-1.5 text-sm text-fg-secondary'>
      {(() => {
        const seen = new Map<string, number>();
        return itemsList.map(item => {
          const raw =
            typeof item === 'string' || typeof item === 'number'
              ? String(item)
              : JSON.stringify(item);
          const count = seen.get(raw) ?? 0;
          seen.set(raw, count + 1);
          return (
            <li key={count > 0 ? `${raw}::${count}` : raw}>
              {renderListItemContent(item)}
            </li>
          );
        });
      })()}
    </ul>
  );
}

function DividerComponent({ props }: { props: Record<string, unknown> }) {
  const { spacing = 'md' } = props;

  const spacingClasses: Record<string, string> = {
    sm: 'my-2',
    md: 'my-4',
    lg: 'my-6',
  };

  return (
    <hr
      className={`border-primary ${spacingClasses[(spacing as string) || 'md']}`}
    />
  );
}
